import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { QueryContentMode, QueryTarget } from "@/shared/domain/query";
import {
  useInfiniteQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import type { SessionRecord } from "@/shared/domain/sessions/SessionRecord";
import type { SearchQueryBody } from "@/types";

import { sessionQueryKeys } from "./sessionQueryKeys";

// The drain effect below fetches every page; large pages keep the number of
// round trips down.
const PICKER_PAGE_SIZE = 100;

export interface UseDatasetSessionsResult {
  sessions: SessionRecord[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * All sessions in a dataset, for the "add files to existing session" picker.
 * Drains the full cursor chain so every session is selectable: `isLoading`
 * stays true until the last page lands, and `error` carries a failure from
 * either the query submit or a page fetch.
 */
export function useDatasetSessions(
  datasetId: string,
): UseDatasetSessionsResult {
  const resolvedOrgId = useCurrentOrgId();

  const query = React.useMemo<SearchQueryBody>(
    () => ({
      condition: {
        field: "dataset.dataset_id",
        comparator: "EQUALS",
        value: datasetId,
      },
    }),
    [datasetId],
  );

  const queryKey = sessionQueryKeys.list(resolvedOrgId, query);

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Sessions,
    contentMode: QueryContentMode.RecordOnly,
    queryKey,
  });

  const resultsQuery = useInfiniteQueryResults<SessionRecord>(
    submitQuery.data?.query_id,
    { pageSize: PICKER_PAGE_SIZE },
  );
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } =
    resultsQuery;

  // The picker shows one flat list, so eagerly drain the cursor chain instead
  // of waiting for a "load more" interaction. Stop on error: `hasNextPage`
  // stays true after a failed page fetch, so refiring would retry forever.
  React.useEffect(
    function loadAllPages() {
      if (hasNextPage && !isFetchingNextPage && !isError) {
        void fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, isError, fetchNextPage],
  );

  return {
    sessions: resultsQuery.data ?? [],
    // `isPlaceholderData` covers a changed `datasetId`: the submit query's
    // `keepPreviousData` then holds the previous dataset's record (and its
    // fully-cached results) while the resubmit is in flight, so without it the
    // hook would report the previous dataset's sessions as settled.
    // `hasNextPage` keeps this true through the render gap between one page
    // settling and the drain effect requesting the next.
    isLoading:
      submitQuery.isLoading ||
      submitQuery.isPlaceholderData ||
      resultsQuery.isLoading ||
      isFetchingNextPage ||
      (hasNextPage && !isError),
    error: submitQuery.error ?? resultsQuery.error,
  };
}
